export interface TrainingExercise {
  id: string;
  label: string;
  name: string;
  sets: number;
  targetReps: string;
  tempo?: string;
  pause?: string;
}

export interface TrainingDay {
  id: string;
  title: string;
  exercises: TrainingExercise[];
}

export interface TrainingPlan {
  id: string;
  title: string;
  days: TrainingDay[];
  compact: boolean;
}

export type TrainingSetField = "reps" | "weight";

const legWorkout: TrainingExercise[] = [
  {
    id: "seated-leg-curl",
    label: "A",
    name: "Seated Leg Curl",
    sets: 2,
    targetReps: "6-10",
    pause: "3min",
  },
  {
    id: "pendulum-squat",
    label: "B",
    name: "Pendulum Squat",
    sets: 2,
    targetReps: "4-8",
    pause: "3min",
  },
  {
    id: "romanian-deadlift",
    label: "C",
    name: "Romanian Deadlift (RDL)",
    sets: 2,
    targetReps: "8-10",
    pause: "3min",
  },
  {
    id: "adduction-machine",
    label: "D",
    name: "Adduction Machine",
    sets: 2,
    targetReps: "8-10",
    pause: "3min",
  },
  {
    id: "leg-extension",
    label: "E",
    name: "Leg Extension",
    sets: 2,
    targetReps: "8-10",
    pause: "2min",
  },
  {
    id: "calf-raises",
    label: "F",
    name: "Calf Raises",
    sets: 2,
    targetReps: "10-15",
    pause: "2min",
  },
  {
    id: "eugene-curls",
    label: "G",
    name: "Eugene Curls",
    sets: 1,
    targetReps: "10-15",
    pause: "1min",
  },
  {
    id: "cable-wrist-curls",
    label: "H",
    name: "Cable Wrist Curls",
    sets: 1,
    targetReps: "10-15",
    pause: "1min",
  },
  {
    id: "cable-reverse-curl",
    label: "I",
    name: "Cable Reverse Curl",
    sets: 1,
    targetReps: "6-8",
    pause: "1min",
  },
];

const newSplitExercise = (
  id: string,
  name: string,
  sets: number,
  targetReps: string
): TrainingExercise => ({ id, label: "", name, sets, targetReps });

export const upperLowerDays: TrainingDay[] = [
  {
    id: "upper-lower-day-1-upper",
    title: "Day 1 - Upper 1",
    exercises: [
      newSplitExercise("machine-chest-press", "Chest Press", 2, "4-6"),
      newSplitExercise("chest-supported-upper-back-row", "Chest Supported Upper Back Row", 2, "6-8"),
      newSplitExercise("recline-curls", "Recline Curls", 2, "8-10"),
      newSplitExercise("tricep-pushdown-in-bench", "Tricep Pushdown Bench", 2, "10-12"),
      newSplitExercise("high-row", "High Row", 2, "8-10"),
      newSplitExercise("machine-lat-raise", "Machine Lat Raise", 2, "8-10"),
      newSplitExercise("smith-machine-incline-bench-press", "Smith Machine Incline Bench Press", 2, "5-7"),
      newSplitExercise("reverse-flies", "Reverse Flies", 2, "6-10"),
      newSplitExercise("cable-crunch", "Cable Crunch", 2, "8-12"),
      newSplitExercise("hanging-leg-raises", "Hanging Leg Raises", 2, "10-15"),
    ],
  },
  {
    id: "upper-lower-day-2-lower",
    title: "Day 2 - Lower 1",
    exercises: legWorkout,
  },
  {
    id: "upper-lower-day-3-upper",
    title: "Day 3 - Upper 2",
    exercises: [
      newSplitExercise("chest-fly", "Chest Fly", 2, "6-10"),
      newSplitExercise("t-bar-row", "T-Bar Row", 2, "6-10"),
      newSplitExercise("incline-dumbbell-press", "Incline Dumbbell Press", 2, "8-10"),
      newSplitExercise("lat-pullover", "Lat Pullover", 2, "8-10"),
      newSplitExercise("shoulder-press", "Shoulder Press", 2, "6-8"),
      newSplitExercise("cable-curl", "Cable Curl", 2, "8-10"),
      newSplitExercise("overhead-cable-extension", "Overhead Cable Extension", 2, "8-10"),
      newSplitExercise("machine-lat-raise", "Machine Lat Raise", 2, "8-10"),
      newSplitExercise("cable-crunch", "Cable Crunch", 2, "8-12"),
      newSplitExercise("hanging-leg-raises", "Hanging Leg Raises", 2, "10-15"),
    ],
  },
  {
    id: "upper-lower-day-4-lower",
    title: "Day 4 - Lower 2",
    exercises: legWorkout,
  },
];

export const trainingPlans: TrainingPlan[] = [
  { id: "upper-lower", title: "Upper / Lower", days: upperLowerDays, compact: true },
];

export const getSetKey = (
  weekId: string,
  dayId: string,
  exerciseId: string,
  setNumber: number,
  field: TrainingSetField = "reps"
) => {
  const baseKey = `week-${weekId}.${dayId}.${exerciseId}.set-${setNumber}`;
  return field === "reps" ? baseKey : `${baseKey}.weight`;
};
